/**
 * Page 2 of the booking flow, the part with no screen (batch-29, item R10; plan §6): what a hotel offers as the rows a
 * guest picks from, how many rooms of a type the page will let them take, the rooms the page pre-picks, the people in
 * each room, and the `booking` object the server is asked with. Nothing here knows how many rooms a hotel has or has
 * left: the server says only "available", "full" or "closed" for a type, and a pick of several rooms is confirmed or
 * refused by the price. Nothing here works out money either — a stay's price is the sum the server sent per night.
 */
import type { BookingOptions, BookingPrice, BookingRequest, PublicAgeGroup, PublicEvent, PublicHotelCard, RoomChoice } from '../public/api';
import { hotelCardsOf, hotelIdOf } from '../public/api';
import { roomChargeOf } from '../public/roomCharge';
import type { SiteImage } from '../public/site';
import { bedDefault, bedValid, twoPerBedOf, type BedSettings, type BedState, type RoomBeds } from './beds';
import { placeParty, roomPeople, type Counts } from './placement';
import { prePick, type Candidate } from './prepick';
import { MAX_PEOPLE, MAX_ROOMS, type Draft, type DraftRoom } from './state';
import { nightCount, type StayRules } from './stayDates';
import { moreFrom } from '../roomTypes/occupancy';
import { sofaBedsOf } from '../roomTypes/beds';
import type { BookingSummary, SummaryLine } from './Summary';

/** One room type of the hotel as page 2 needs it. */
export interface TypeInfo {
  code: string; label: string; closed: boolean; group: string | null;
  /** The bed numbers; `beds.beds === null` is a type nobody has set, which takes any party. */
  beds: RoomBeds; settings: BedSettings;
  /** How many sofa beds the room has (item BI1): shown as an icon on the card, never counted by the bed rule. Missing or 0 = none. */
  sofaBeds?: number;
  photo: SiteImage | null;
  /** What the hotel says about the type, and every picture of it, as the anonymous read sent them (item R22). */
  description?: string | null; photos?: SiteImage[];
  /** The price of the stay when rooms are charged and every night has a price; null otherwise. */
  stayCents: number | null;
  /** The head count from which this type costs more per night; null (or missing) when it never does. Words on the card only — the server charges. */
  moreFrom?: number | null;
  order: number;
}
/**
 * One line of the list the guest picks from: a room type, or — where the hotel's types are in groups — a group.
 * `choose` is true where the guest may then name a type of the group on the room itself ("Which Double?").
 */
export interface Row { key: string; kind: 'type' | 'group'; name: string; heading: string | null; members: TypeInfo[]; choose: boolean; given: string | null }
export interface HotelRooms {
  types: TypeInfo[]; rows: Row[]; byCode: Map<string, TypeInfo>;
  /** True when rooms are charged from the event's room prices. */
  charged: boolean; cotCents: number; cribCents: number;
  /** The hotel's own settings, for placing a party across rooms of several types. */
  settings: BedSettings;
}
/** How many rooms of a type the page lets this guest take, by type code. Never shown. */
export type Caps = Record<string, number>;
export type TypeStatus = BookingOptions['types'][number]['status'];

const NOT_SET: RoomBeds = { beds: null, bedsForTwo: 0, maxPeople: MAX_PEOPLE, maxCots: 0, maxCribs: 0 };
const sum = (c: Counts): number => Object.values(c).reduce((a, b) => a + Math.max(0, b), 0);
const same = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** The nights of a stay, first to last, as days. */
export function stayNights(arrive: string, leave: string): string[] {
  const n = nightCount(arrive, leave);
  const out: string[] = [];
  const d = new Date(`${arrive}T00:00:00Z`);
  for (let i = 0; i < n && !Number.isNaN(d.getTime()); i += 1) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/** The hotel a booking is for: the one chosen, or the event's only hotel. Null while a guest of a several-hotel event has not chosen. */
export function hotelOf(event: PublicEvent, hotelId: string | null | undefined): PublicHotelCard | null {
  const cards = hotelCardsOf(event);
  if (cards.length === 1) return cards[0]!;
  return cards.find((h) => h.id === hotelId) ?? null;
}

/** What one hotel offers for a stay: its types, and the rows they are picked from. */
export function hotelRooms(event: PublicEvent, hotel: PublicHotelCard | null, stay: { arrive: string; leave: string }): HotelRooms {
  const charged = roomChargeOf(event) !== null;
  const nights = stayNights(stay.arrive, stay.leave);
  const eventTotals = new Map((roomChargeOf(event)?.hotels.find((h) => h.hotel_id === hotel?.id)?.types ?? []).map((t) => [t.type, t.priced ? t.stay_total_cents : null]));
  const settings: BedSettings = {
    cribsPerCot100: hotel?.beds?.cribs_per_cot_x100 ?? 200, babiesCount: hotel?.beds?.babies_count ?? true, twoPerBed: twoPerBedOf(event.beds?.share),
  };
  const types: TypeInfo[] = (hotel?.room_types ?? []).map((t, order) => {
    let stayCents: number | null = null;
    if (charged) {
      if (t.nights && nights.length) {
        const each = nights.map((d) => t.nights![d]?.p ?? null);
        stayCents = each.every((p): p is number => typeof p === 'number') ? each.reduce((a, b) => a + b, 0) : null;
      } else stayCents = eventTotals.get(t.type) ?? null;
    }
    const b = t.beds;
    return {
      code: t.type, label: t.label, closed: t.closed, group: typeof t.group === 'string' && t.group.trim() ? t.group.trim() : null,
      beds: b ? { beds: b.beds, bedsForTwo: b.beds_for_two, maxPeople: b.max_people, maxCots: b.max_cots, maxCribs: b.max_cribs } : NOT_SET,
      settings: { ...settings, babiesCount: b?.babies_count ?? settings.babiesCount, cribsPerCot100: b?.cribs_per_cot_x100 ?? settings.cribsPerCot100 },
      photo: (t.photos ?? [])[0] ?? null, description: typeof t.description === 'string' && t.description.trim() ? t.description.trim() : null, photos: Array.isArray(t.photos) ? t.photos : [],
      stayCents, order,
      // the key is there only for a room that has a sofa bed
      ...(b && sofaBedsOf(b.sofa_beds) > 0 ? { sofaBeds: sofaBedsOf(b.sofa_beds) } : {}),
      // only while the event charges for it; the key is absent otherwise, as it always was
      ...(charged && event.occupancy_charge === true && moreFrom(t.occupancy) !== null ? { moreFrom: moreFrom(t.occupancy) } : {}),
    };
  });
  const byCode = new Map(types.map((t) => [t.code, t]));
  return {
    types, rows: rowsOf(types, hotel?.room_choice ?? (hotelCardsOf(event).length === 1 ? event.room_choice : null) ?? null), byCode, charged,
    cotCents: hotel?.beds?.cot_cents ?? 0, cribCents: hotel?.beds?.crib_cents ?? 0, settings,
  };
}

/**
 * The rows, in the hotel's own order (a group sits where its first type sat), for the three ways an event lets guests
 * choose a grouped room — the same three as the one-page form: the group only; every type under its group's name; the
 * group, then the type. With no groups every type is its own row.
 */
export function rowsOf(types: readonly TypeInfo[], choice: RoomChoice | null): Row[] {
  const byCode = new Map(types.map((t) => [t.code, t]));
  const groups = (Array.isArray(choice?.groups) ? choice.groups : [])
    .map((g) => ({ g, members: (Array.isArray(g.types) ? g.types : []).flatMap((c) => (byCode.has(c) ? [byCode.get(c)!] : [])) }))
    .filter((x) => x.members.length > 0 && typeof x.g.name === 'string' && x.g.name.trim() !== '');
  const groupOf = new Map<string, (typeof groups)[number]>();
  for (const x of groups) for (const m of x.members) if (!groupOf.has(m.code)) groupOf.set(m.code, x);
  const how = choice?.mode ?? 'one_step';
  const rows: Row[] = [];
  const done = new Set<(typeof groups)[number]>();
  for (const t of types) {
    const x = groupOf.get(t.code);
    if (!x) { rows.push({ key: `t:${t.code}`, kind: 'type', name: t.label, heading: null, members: [t], choose: false, given: null }); continue; }
    const name = x.g.name.trim();
    if (how === 'one_step') { rows.push({ key: `t:${t.code}`, kind: 'type', name: t.label, heading: name, members: [t], choose: false, given: null }); continue; }
    if (done.has(x)) continue;
    done.add(x);
    rows.push({ key: `g:${name}`, kind: 'group', name, heading: null, members: x.members, choose: how === 'two_step' && x.members.length > 1, given: x.g.assigned_type ?? x.g.billing_type ?? null });
  }
  return rows;
}

/** The row a room was picked from. */
export function rowOfRoom(rows: readonly Row[], room: DraftRoom): Row | null {
  if (room.group) return rows.find((r) => r.kind === 'group' && same(r.name, room.group)) ?? null;
  return rows.find((r) => r.kind === 'type' && r.members[0]!.code === room.type) ?? null;
}

/** What a room is called on its card, in the summary and on page 4: its type — or its group, when it was picked by the group and the guest named no type of it. */
export function roomLabelOf(h: HotelRooms, room: DraftRoom): string {
  const row = rowOfRoom(h.rows, room);
  return row?.kind === 'group' && (room.any || !row.choose) ? row.name : h.byCode.get(room.type)?.label ?? room.type;
}

/** G3c: what a room is called wherever a guest sees it: "Shared room" for a shared place — never its type, nor a group's name — else `roomLabelOf`. */
export const shownRoomLabel = (h: HotelRooms, room: DraftRoom): string => (room.share ? SHARED_LABEL : roomLabelOf(h, room));

/** True when page 2 is in order as the draft has it: a hotel, rooms of its types, and the whole party in them with nobody twice. The server still has the last word. */
export function roomsDone(event: PublicEvent, draft: Pick<Draft, 'hotelId' | 'rooms' | 'groups'>): boolean {
  const hotel = hotelOf(event, draft.hotelId);
  const rooms = draft.rooms ?? [];
  if (!hotel || !rooms.length) return false;
  const codes = new Set(hotel.room_types.map((t) => t.type));
  return rooms.every((r) => codes.has(r.type) && sum(r.people) > 0) && sum(poolOf(rooms, draft.groups)) === 0 && !overPlaced(rooms, draft.groups);
}

/**
 * How many rooms of each type the page lets this guest take. The server's answer carries no count: a type it calls
 * available gets as many as the party could ever use (`most`), and each time the price refuses a pick for a type that
 * number is brought down (`lowered`). Every other type, and a type with a night that has no price, gets none.
 */
export function capsOf(h: HotelRooms, statuses: BookingOptions['types'], lowered: Caps, most: number): Caps {
  const status = new Map(statuses.map((t) => [t.code, t.status]));
  const caps: Caps = {};
  for (const t of h.types) {
    const open = !t.closed && status.get(t.code) === 'available' && (!h.charged || t.stayCents !== null);
    caps[t.code] = open ? Math.max(0, Math.min(most, lowered[t.code] ?? most)) : 0;
  }
  return caps;
}

/** The word on a type that cannot be picked: "Full" or "Closed"; null for one that can. */
export function typeTag(t: TypeInfo, statuses: BookingOptions['types'], caps: Caps): 'Full' | 'Closed' | null {
  if ((caps[t.code] ?? 0) > 0) return null;
  const s = statuses.find((x) => x.code === t.code)?.status;
  return t.closed || s === 'closed' || s === 'not_sold' || s === undefined ? 'Closed' : 'Full';
}
export function rowTag(row: Row, statuses: BookingOptions['types'], caps: Caps): 'Full' | 'Closed' | null {
  const tags = row.members.map((m) => typeTag(m, statuses, caps));
  if (tags.some((t) => t === null)) return null;
  return tags.some((t) => t === 'Full') ? 'Full' : 'Closed';
}

/**
 * The type a guest joins the waiting list for from a line that reads "Full": the first of its types the server itself
 * calls full. Null when none is — a line is also "Full" once the guest's own pick was brought down, and a room that can
 * still be booked is not waited for.
 */
export function waitlistType(row: Row, statuses: BookingOptions['types']): TypeInfo | null {
  return row.members.find((m) => !m.closed && statuses.find((x) => x.code === m.code)?.status === 'full') ?? null;
}
/** The people of a waiting-list request on one line: "Adults 2, Children 1". */
export function partyLine(people: Counts, groups: readonly PublicAgeGroup[]): string {
  return [...groups].reverse().filter((g) => (people[g.key] ?? 0) > 0).map((g) => `${g.name} ${people[g.key]}`).join(', ');
}

/** A group's types in the order a room of the group is given one: the server's choice first, then the lowest stay price, then the hotel's order. */
function givenOrder(row: Row): TypeInfo[] {
  return [...row.members].sort((a, b) => (Number(b.code === row.given) - Number(a.code === row.given)) || ((a.stayCents ?? 0) - (b.stayCents ?? 0)) || (a.order - b.order));
}
/** The type a room picked by its group is given: the first of the group's order that still has room under the caps. Null when none has. */
export function givenType(row: Row, caps: Caps, used: Counts): TypeInfo | null {
  return givenOrder(row).find((m) => (used[m.code] ?? 0) < (caps[m.code] ?? 0)) ?? null;
}

/**
 * The rooms as the caps allow them: a room past its type's cap is dropped (the last of that type first), and a room
 * picked by its group moves to another type of the group when its own has no room. `dropped` says a room went.
 */
export function fitToCaps(rooms: readonly DraftRoom[], h: HotelRooms, caps: Caps): { rooms: DraftRoom[]; dropped: boolean; changed: boolean } {
  const used: Counts = {};
  const out: DraftRoom[] = [];
  let dropped = false;
  let changed = false;
  // rooms with a named type keep it first; a room of "any type" takes what is then free
  const named = rooms.map((r) => !r.any);
  const keep: Array<DraftRoom | null> = rooms.map(() => null);
  rooms.forEach((r, i) => {
    if (!named[i]) return;
    // G3c (A2): a shared place is not held to the cap: a part-filled room can still take a sharer, and the server says when it cannot
    if (h.byCode.has(r.type) && (r.share || (used[r.type] ?? 0) < (caps[r.type] ?? 0))) { used[r.type] = (used[r.type] ?? 0) + 1; keep[i] = r; }
  });
  rooms.forEach((r, i) => {
    if (named[i]) return;
    const row = rowOfRoom(h.rows, r);
    const stays = h.byCode.has(r.type) && (used[r.type] ?? 0) < (caps[r.type] ?? 0);
    const t = stays ? h.byCode.get(r.type)! : row ? givenType(row, caps, used) : null;
    if (!t) return;
    used[t.code] = (used[t.code] ?? 0) + 1;
    if (t.code === r.type) keep[i] = r;
    else { keep[i] = { type: t.code, people: r.people, group: r.group, any: true, ...(r.share ? { share: r.share } : {}) }; changed = true; }
  });
  keep.forEach((r) => { if (r) out.push(r); else { dropped = true; changed = true; } });
  return { rooms: out, dropped, changed };
}

export interface RoomView {
  type: TypeInfo;
  /** nobody is in the room */
  empty: boolean;
  /** what the rule adds for these people; null when the room cannot take them */
  def: BedState | null;
  /** the cots and cribs the room has now: the guest's own when the rule allows them, else what the rule adds */
  state: BedState;
  valid: boolean;
}
/** A room as the bed rule sees it. */
export function viewRoom(room: DraftRoom, type: TypeInfo, cribKey: string | null): RoomView {
  const none = { cots: 0, cribs: 0 };
  if (sum(room.people) === 0) return { type, empty: true, def: none, state: none, valid: false };
  const p = roomPeople(room.people, cribKey);
  const def = bedDefault(type.beds, type.settings, p);
  if (!def) return { type, empty: false, def: null, state: none, valid: false };
  const own = { cots: room.cots ?? def.cots, cribs: room.cribs ?? def.cribs };
  const state = bedValid(type.beds, type.settings, p, own) ? own : def;
  return { type, empty: false, def, state, valid: true };
}

/** The people the rooms do not hold yet, per group; a group with more people in rooms than in the party reads 0 here (see `overPlaced`). */
export function poolOf(rooms: readonly DraftRoom[], party: Counts): Counts {
  const pool: Counts = {};
  for (const [k, n] of Object.entries(party)) {
    const left = n - rooms.reduce((a, r) => a + (r.people[k] ?? 0), 0);
    if (left > 0) pool[k] = left;
  }
  return pool;
}
/** True when the rooms hold someone the party does not have (the party changed after the rooms were filled). */
export function overPlaced(rooms: readonly DraftRoom[], party: Counts): boolean {
  const placed: Counts = {};
  for (const r of rooms) for (const [k, n] of Object.entries(r.people)) placed[k] = (placed[k] ?? 0) + n;
  return Object.entries(placed).some(([k, n]) => n > (party[k] ?? 0));
}

/** Spread the whole party across the rooms again; the guest's own cot and crib changes are dropped with it (a shared place, G3, stays). */
export function placeAll(rooms: readonly DraftRoom[], h: HotelRooms, party: Counts, groupOrder: readonly string[], cribKey: string | null): DraftRoom[] {
  const placed = placeParty(rooms.map((r) => h.byCode.get(r.type)?.beds ?? NOT_SET), h.settings, party, groupOrder, cribKey);
  return rooms.map((r, i) => {
    const people: Counts = {};
    for (const [k, n] of Object.entries(placed.rooms[i]?.people ?? {})) if (n > 0) people[k] = n;
    return { type: r.type, people, ...(r.group ? { group: r.group, ...(r.any ? { any: true } : {}) } : {}), ...(r.share ? { share: r.share } : {}) };
  });
}

/** One person of a group goes into or out of a room. The room's own cot and crib changes are dropped: the rule runs again (a shared place, G3, stays). */
export function movePerson(rooms: readonly DraftRoom[], index: number, key: string, by: 1 | -1): DraftRoom[] {
  return rooms.map((r, i) => {
    if (i !== index) return r;
    const n = Math.max(0, (r.people[key] ?? 0) + by);
    const people = { ...r.people };
    if (n > 0) people[key] = n; else delete people[key];
    return { type: r.type, people, ...(r.group ? { group: r.group, ...(r.any ? { any: true } : {}) } : {}), ...(r.share ? { share: r.share } : {}) };
  });
}
/** Whether one more person of a group may go into a room. */
export function takesOneMore(room: DraftRoom, type: TypeInfo, key: string, cribKey: string | null): boolean {
  return bedDefault(type.beds, type.settings, roomPeople({ ...room.people, [key]: (room.people[key] ?? 0) + 1 }, cribKey)) !== null;
}

export interface Picked { rooms: DraftRoom[]; needed: number }
/**
 * The rooms the page picks for a party: as if every type the server called available had the rooms asked for, within
 * the caps. A group is offered as the type a room of it would be given. Null = nothing fits; the guest chooses by hand.
 */
export function pickRooms(h: HotelRooms, caps: Caps, asked: number, party: Counts, groupOrder: readonly string[], cribKey: string | null, maxRooms: number = MAX_ROOMS): Picked | null {
  const from = new Map<string, Row>();
  const candidates: Candidate[] = [];
  for (const row of h.rows) {
    const t = row.kind === 'group' ? givenType(row, caps, {}) : row.members[0]!;
    if (!t || (caps[t.code] ?? 0) < 1 || from.has(t.code)) continue;
    from.set(t.code, row);
    candidates.push({ code: t.code, beds: t.beds, upTo: caps[t.code]!, stayCents: t.stayCents, order: t.order, ...(t.sofaBeds ? { sofaBeds: t.sofaBeds } : {}) });
  }
  const got = prePick(candidates, asked, party, h.settings, groupOrder, cribKey, maxRooms);
  if (!got) return null;
  const rooms = got.rooms.map((code, i): DraftRoom => {
    const row = from.get(code)!;
    const people: Counts = {};
    for (const [k, n] of Object.entries(got.placement.rooms[i]!.people)) if (n > 0) people[k] = n;
    return { type: code, people, ...(row.kind === 'group' ? { group: row.name, any: true } : {}) };
  });
  return { rooms, needed: got.needed };
}

/** One more room from a row, empty. Null when the row has none to give or the booking already has as many rooms as it may (ten unless the event sets its own). */
export function addRoom(rooms: readonly DraftRoom[], row: Row, caps: Caps, most: number, maxRooms: number = MAX_ROOMS): DraftRoom[] | null {
  if (rooms.length >= Math.min(maxRooms, most)) return null;
  const used: Counts = {};
  for (const r of rooms) used[r.type] = (used[r.type] ?? 0) + 1;
  if (row.kind === 'type') {
    const t = row.members[0]!;
    return (used[t.code] ?? 0) < (caps[t.code] ?? 0) ? [...rooms, { type: t.code, people: {} }] : null;
  }
  const t = givenType(row, caps, used);
  return t ? [...rooms, { type: t.code, people: {}, group: row.name, any: true }] : null;
}
/** One room fewer from a row: its last one. */
export function dropRoom(rooms: readonly DraftRoom[], row: Row): DraftRoom[] {
  for (let i = rooms.length - 1; i >= 0; i -= 1) {
    if (rowOfRoom([row], rooms[i]!)) return rooms.filter((_, k) => k !== i);
  }
  return [...rooms];
}
export const roomsOfRow = (rooms: readonly DraftRoom[], row: Row): number => rooms.filter((r) => rowOfRoom([row], r)).length;

/** The part of the `booking` object every request of page 2 shares. The dates are left out on a fixed-date event. */
export function bookingBase(event: PublicEvent, rules: StayRules, draft: Draft, stay: { arrive: string; leave: string }, hotelId: string | null): Omit<BookingRequest, 'v' | 'mode'> {
  const groups: Counts = {};
  for (const [k, n] of Object.entries(draft.groups)) if (n > 0) groups[k] = n;
  const hotel = hotelIdOf(event, hotelId);
  return { ...(hotel ? { hotel_id: hotel } : {}), ...(rules.mode === 'fixed' ? {} : { check_in: stay.arrive, check_out: stay.leave }), groups };
}
/**
 * The rooms as the server is asked to price them: the type, who is in it, and the exact cots and cribs on the page —
 * and the group, for a room picked by its group whose type the guest did not name (the server keeps it on its people).
 * G3: the one place a request carries `share` — the sharer's gender, for a place in a shared room the guest chose; the
 * quote and the submit both send what this returns, so the two never differ. A room with no share is sent as it always was.
 */
export function bookingRooms(rooms: readonly DraftRoom[], h: HotelRooms, cribKey: string | null): NonNullable<BookingRequest['rooms']> {
  return rooms.map((r) => {
    const t = h.byCode.get(r.type);
    const v = t ? viewRoom(r, t, cribKey) : null;
    const set = Boolean(t && t.beds.beds !== null);
    const row = rowOfRoom(h.rows, r);
    const group = row?.kind === 'group' && (r.any || !row.choose) ? row.name : null;
    const share = r.share === 'M' || r.share === 'F' ? r.share : null;
    return { type: r.type, people: r.people, ...(v && set ? { cots: v.state.cots, cribs: v.state.cribs } : {}), ...(group ? { group } : {}), ...(share ? { share } : {}) };
  });
}

/** "Adults 2 · Infants 1": who is in a room, oldest group first. */
export function peopleLine(people: Counts, groups: readonly PublicAgeGroup[]): string {
  return [...groups].reverse().filter((g) => (people[g.key] ?? 0) > 0).map((g) => `${g.name} ${people[g.key]}`).join(' · ');
}

const BED_LINES = new Set(['room_price', 'cot', 'crib', 'processing_fee', 'occupancy', 'room_discount']);
/** G1 (rmp-new): what a sharer's room name ends with: "Room N · Shared room, your place". The people-per-room count is not said. */
export const SHARED_PLACE = ', your place';
/** G3/G1: what a room's name ends with when it is one place in a shared room; nothing for a whole room. `_perRoom` is no longer shown (G1). */
export const sharedSuffix = (room: DraftRoom | undefined, _perRoom?: number | null): string => (room?.share ? SHARED_PLACE : '');
/** G3c: what a guest sees for a place in a shared room, wherever a type's name would be: the type itself is never named to them. */
export const SHARED_LABEL = 'Shared room';
/**
 * G3d/G1: a stored shared room line ("Shared room — 2 nights (1 of 2)", 0165) as the guest reads it: "Shared room, your place —
 * 2 nights". Display only; the stored name, the emails and the organizer's views keep the stored text. Any other name is unchanged.
 */
export const guestRoomLineName = (name: string): string => {
  const m = /^Shared room — (.+?) \(1 of \d+\)$/.exec(name);
  return m ? `${SHARED_LABEL}${SHARED_PLACE} — ${m[1]}` : name;
};
/** G3c-2: the share a waiting-list request carries: only on the shared place's own type, never on another type's or a whole hotel's list. */
export const waitlistShareOf = (rooms: readonly DraftRoom[], type: string | null): 'M' | 'F' | null => {
  const r = rooms[0];
  return r?.share && type !== null && r.type === type ? r.share : null;
};
/** G3c: the one type of a hotel the organizer chose for shared places (the server allows one per hotel); null when none. */
export const sharedTypeOf = (hotel: PublicHotelCard | null | undefined) => hotel?.room_types.find((t) => t.shareable) ?? null;
/** The server's price as the summary shows it. Every figure is the server's own; nothing is added up here. `perRoom`: the event's people per shared room (G3). */
export function summaryOf(q: BookingPrice, rooms: readonly DraftRoom[], groups: readonly PublicAgeGroup[], currency: string, perRoom?: number | null): BookingSummary | null {
  const b = q.booking;
  if (!b || q.room_error || typeof q.total_cents !== 'number') return null;
  // a promo code's discount is a line of its own, the server's figure, shown as an amount taken off
  const fees: SummaryLine[] = q.discount_cents ? [{ label: q.coupon?.code ? `Promo code ${q.coupon.code}` : 'Promo code', cents: -q.discount_cents }] : [];
  if (q.fee_cents) fees.push({ label: 'Fees', cents: q.fee_cents });
  if (q.processing_fee_cents) fees.push({ label: q.processing_fee_name || 'Processing fee', cents: q.processing_fee_cents });
  const taxes: SummaryLine[] = (q.tax_lines ?? []).length
    ? q.tax_lines!.map((t) => ({ label: t.name, cents: t.cents }))
    : q.tax_cents ? [{ label: 'Tax', cents: q.tax_cents }] : [];
  return {
    arrive: b.check_in, leave: b.check_out, nights: b.nights, currency,
    rooms: b.rooms.map((r) => ({
      name: `Room ${r.position} · ${rooms[r.position - 1]?.share ? SHARED_LABEL : r.label}${sharedSuffix(rooms[r.position - 1], perRoom)}`, people: peopleLine(rooms[r.position - 1]?.people ?? {}, groups), cents: r.room_cents,
      // the cots and cribs the server priced this room with, for the icons beside its name; the keys are there only when it has one
      ...(r.cots > 0 ? { cots: r.cots } : {}), ...(r.cribs > 0 ? { cribs: r.cribs } : {}),
      lines: [
        // more people in the room: the server's own line and figure, under the room it is for
        ...(q.lines ?? []).filter((l) => l.item_id === 'occupancy' && (l as { position?: number }).position === r.position).map((l) => ({ label: l.name, cents: l.total_cents })),
        ...(r.cots > 0 ? [{ label: r.cots === 1 ? 'Cot' : `${r.cots} cots`, cents: r.cot_cents }] : []),
        ...(r.cribs > 0 ? [{ label: r.cribs === 1 ? 'Crib' : `${r.cribs} cribs`, cents: r.crib_cents }] : []),
        // a room after the first of the booking, on an event that takes an amount off it (0122): the server's line and figure
        ...(q.lines ?? []).filter((l) => l.item_id === 'room_discount' && (l as { position?: number }).position === r.position).map((l) => ({ label: 'Extra room discount', cents: l.total_cents })),
      ],
    })),
    extras: (q.lines ?? []).filter((l) => !BED_LINES.has(l.item_id)).map((l) => ({ label: l.name, cents: l.total_cents })),
    fees, taxes, totalCents: q.total_cents, dueTodayCents: q.charge_cents ?? q.total_cents,
  };
}

/** Which hotels the list shows: every one, or — when the organizer lists only hotels with rooms — the available ones, and a full one only while the booking flow's waiting list is on. A closed hotel is then not listed. */
export function listedHotels(event: PublicEvent, statuses: BookingOptions['hotels']): Array<{ hotel: PublicHotelCard; status: 'available' | 'full' | 'closed' }> {
  const by = new Map(statuses.map((s) => [s.id, s.status]));
  const all = hotelCardsOf(event).map((hotel) => ({ hotel, status: by.get(hotel.id) ?? ('closed' as const) }));
  if (event.site?.hotel_list !== 'available') return all;
  return all.filter((x) => x.status === 'available' || (x.status === 'full' && event.booking_waitlist === true));
}
